import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useOutletContext } from "react-router-dom";
import { Save } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { api, apiError } from "../../lib/api";

export default function Settings() {
  const { refreshHospital } = useOutletContext() || {};
  const [form, setForm] = useState({ name: "", address: "", phone: "", email: "", logo_url: "" });
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get("/hospital/me").then((r) => {
      setForm({
        name: r.data.name || "",
        address: r.data.address || "",
        phone: r.data.phone || "",
        email: r.data.email || "",
        logo_url: r.data.logo_url || "",
      });
      setLoaded(true);
    });
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = Object.fromEntries(Object.entries(form).filter(([, v]) => v !== ""));
      await api.patch("/hospital/me", payload);
      toast.success("Hospital updated");
      refreshHospital?.();
    } catch (err) {
      toast.error(apiError(err, "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 max-w-2xl" data-testid="settings-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-teal-400">◖ settings</div>
        <h1 className="mt-2 text-3xl font-light tracking-tight">Hospital profile</h1>
        <p className="text-sm text-mb-fg2 mt-1">Visible to patients in Phase 3 booking.</p>
      </div>

      {loaded && (
        <form onSubmit={submit} className="rounded-xl border border-mb-border bg-mb-surface p-6 grid sm:grid-cols-2 gap-4">
          <Field label="Hospital name" testid="settings-name" value={form.name} required
            onChange={(v) => setForm({ ...form, name: v })} />
          <Field label="Public email" type="email" testid="settings-email" value={form.email}
            onChange={(v) => setForm({ ...form, email: v })} />
          <Field label="Phone" testid="settings-phone" value={form.phone}
            onChange={(v) => setForm({ ...form, phone: v })} />
          <Field label="Logo URL" testid="settings-logo" value={form.logo_url}
            onChange={(v) => setForm({ ...form, logo_url: v })} />
          <div className="sm:col-span-2">
            <Field label="Address" testid="settings-address" value={form.address}
              onChange={(v) => setForm({ ...form, address: v })} />
          </div>
          <div className="sm:col-span-2 flex justify-end">
            <Button type="submit" disabled={saving} data-testid="settings-save"
              className="bg-teal-500 hover:bg-teal-400 text-zinc-950 rounded-lg h-10 px-5 font-medium">
              <Save className="h-4 w-4 mr-2" /> {saving ? "Saving…" : "Save changes"}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}

function Field({ label, type = "text", value, onChange, required, testid }) {
  return (
    <div className="space-y-2">
      <Label className="text-xs uppercase tracking-[0.2em] text-mb-fg2">{label}</Label>
      <Input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} required={required}
        data-testid={testid}
        className="bg-mb-surface2 border-mb-border text-white h-11 rounded-lg focus-visible:ring-teal-500" />
    </div>
  );
}
